import { useState, useEffect } from 'react'
import logoImg from '../assets/logo.png'
import { plansApi } from '../api/client'
import { handleSilentError } from '../utils/errorHandler'

// ── Design tokens (values owned by tailwind.config.js / styles/index.css) ──
const A = 'var(--color-brand-700)'
const A2 = 'var(--color-brand-800)'
const PAPER = 'var(--color-paper)'
const INK = 'var(--color-ink-900)'
const HAIR = 'var(--color-line)'
const HAIR_L = 'var(--color-line-light)'
const MUTED = 'var(--color-ink-500)'
const SOFT = 'var(--color-ink-600)'
const FAINT = 'var(--color-ink-400)'
const SECTION_ALT = 'var(--color-paper-200)'
const ACCENT_SHADOW = 'var(--shadow-brand)'

// ── Data ──
const features = [
  { n: '01', title: '双模型交叉验证', desc: '文档、视频、图片各由第二个不同模型族的独立大模型重新分析并比对一致性；无独立模型或调用失败时降级为结构一致性校验，并显式标记未经交叉验证。', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
  { n: '02', title: 'WORM 防篡改保险库', desc: 'SHA-256 哈希锁定，写入即永恒。满足法律、医疗、监控场景的留存与审计要求。', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
  { n: '03', title: '三模态混合搜索', desc: '语义 + 关键词 + 以图搜图，RRF 融合排序 + AI 重排序，快速定位任何素材。', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' },
  { n: '04', title: '7 步 AI 记忆蒸馏', desc: '多模态提取 → 行业检测 → 内容分类 → Schema 抽取 → 交叉验证 → 知识注入 → 向量化。', icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z' },
  { n: '05', title: '3 级智能分层存储', desc: '按访问频次自动划分热（30 天内）、温（30-90 天）、冷归档（90 天以上）。冷归档 ¥0.015/GB/月，为热存储单价的 1/8，存储适配器支持 OSS / TOS / 本地。', icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4' },
  { n: '06', title: '10 层安全防御', desc: 'TLS → JWT → RBAC → 多租户隔离 → 限速 → 文件扫描 → 反幻觉 → WORM → GDPR → 容器隔离。', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
]

const useCases = [
  { icon: 'M7.5 21l3-7.5 3 7.5M9.75 6.75h.01M13.5 4.5h.01M17.25 6.75h.01M18.75 12h.01M18.75 17.25h.01M15.75 19.5h.01M11.25 19.5h.01M6.75 17.25h.01M5.25 12h.01M5.25 6.75h.01M8.25 4.5h.01M2.25 12a9.75 9.75 0 1119.5 0 9.75 9.75 0 01-19.5 0z', title: '品牌方 / 咨询机构', desc: '品牌资产集中管理，AI 深度分析构建品牌画像，交叉验证标注一致性差异。', save: '替代多套 DAM 工具的重复维护' },
  { icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z', title: 'MCN / 内容团队', desc: '海量视频图片素材管理，以图搜图定位素材，批量记忆注入自动标签。', save: '素材查找从翻目录变为一句话检索' },
  { icon: 'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0118.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9zM15 13a3 3 0 11-6 0 3 3 0 016 0z', title: '视频监控 / 安防', desc: '冷归档单价为热存储的 1/8，WORM 防篡改保留录像原始性。', save: '留存周期越长，分层收益越明显' },
  { icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', title: '医疗机构', desc: '影像与病历资料归档，冷热分层 + WORM 封存，访问留痕可追溯。', save: '写入后不可篡改，留存可审计' },
  { icon: 'M12 3v18M5 21h14M12 6l-6 2m6-2l6 2M6 8l-3 6h6l-3-6zm12 0l-3 6h6l-3-6z', title: '律所 / 会计所', desc: '合同案卷 WORM 封存，全文检索定位，审计日志满足合规要求。', save: '无需自建与运维本地存储' },
  { icon: 'M3 21h18M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16M9 9h1m4 0h1M9 13h1m4 0h1', title: '中小企业备份', desc: 'WORM 防勒索 + 冷热分层，3-2-1 备份策略可落地。', save: '按套餐容量与用量计费' },
]

// ── Helpers ──
function Accent({ children }) {
  return <span style={{ background: `linear-gradient(135deg, ${A}, ${A2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{children}</span>
}

const serif = "'Noto Serif SC', 'Songti SC', Georgia, serif"
const sans = "'Inter', -apple-system, 'PingFang SC', sans-serif"

// ══════════════════════════════════════════════════
export default function Landing({ onOpenLogin }) {
  const [planGroups, setPlanGroups] = useState(null)
  const [plansError, setPlansError] = useState(false)
  const [activeGroup, setActiveGroup] = useState('')

  useEffect(() => {
    let cancelled = false
    plansApi.listPlans()
      .then((data) => {
        if (cancelled) return
        const groups = data?.groups
        if (groups && Object.keys(groups).length > 0) {
          setPlanGroups(groups)
          setActiveGroup(Object.keys(groups)[0])
        } else {
          setPlansError(true)
        }
      })
      .catch((e) => {
        handleSilentError('Landing.listPlans', e)
        if (!cancelled) setPlansError(true)
      })
    return () => { cancelled = true }
  }, [])

  const groupKeys = planGroups ? Object.keys(planGroups) : []
  const plans = planGroups?.[activeGroup]?.plans || []

  return (
    <div style={{ fontFamily: sans, color: INK, background: PAPER }} className="min-h-screen">

      {/* ═══ NAV ═══ */}
      <nav className="sticky top-0 z-40 backdrop-blur-md border-b" style={{ background: PAPER, borderColor: HAIR }}>
        <div className="max-w-[1080px] mx-auto px-6 h-[60px] flex items-center justify-between">
          <img src={logoImg} alt="全景OmniTuring" className="h-9" />
          <button
            onClick={onOpenLogin}
            className="px-5 py-2 text-sm font-medium text-white rounded-full transition-all duration-300"
            style={{ background: `linear-gradient(135deg, ${A}, ${A2})`, boxShadow: ACCENT_SHADOW }}
          >
            登录 / 注册
          </button>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1080px] mx-auto px-6 pt-20 pb-24 text-center">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm mb-10 border"
            style={{ background: PAPER, borderColor: HAIR, color: MUTED }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: A }} />
            双模型交叉验证 + WORM 防篡改的品牌资产平台
          </div>

          {/* Headline */}
          <h1 className="text-[40px] sm:text-[52px] lg:text-[60px] font-extrabold leading-[1.15] tracking-tight mb-8" style={{ fontFamily: serif }}>
            让 AI <Accent>理解</Accent>你的每一份品牌资产
            <br />
            让数据安全<Accent>写入即永恒</Accent>
          </h1>

          {/* Sub */}
          <p className="text-lg leading-relaxed max-w-[640px] mx-auto mb-12" style={{ color: MUTED }}>
            全景 OmniTuring 融合双模型交叉验证能力与 WORM 防篡改存储技术，
            为品牌方、创作者和企业提供 AI 原生的数字资产管理与安全存储解决方案。
          </p>

          {/* CTAs */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={onOpenLogin}
              className="px-8 py-3.5 text-lg font-semibold text-white rounded-lg transition-all duration-300"
              style={{ background: `linear-gradient(135deg, ${A}, ${A2})`, boxShadow: ACCENT_SHADOW }}
            >
              免费开始使用
            </button>
            <a
              href="#pricing"
              className="px-8 py-3.5 text-lg font-semibold rounded-lg border transition-all duration-200"
              style={{ borderColor: HAIR, color: MUTED, background: PAPER }}
            >
              查看定价
            </a>
          </div>
        </div>
        {/* Decorative line */}
        <div className="max-w-[120px] mx-auto h-px" style={{ background: `linear-gradient(90deg, transparent, ${A}, transparent)` }} />
      </section>

      {/* ═══ FEATURES ═══ */}
      <section className="py-24" style={{ background: PAPER }} id="features">
        <div className="max-w-[1080px] mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-medium tracking-[0.12em] uppercase mb-3" style={{ color: A }}>Core Capabilities</p>
            <h2 className="text-[32px] font-bold" style={{ fontFamily: serif }}>六大核心技术能力</h2>
            <p className="text-lg mt-3" style={{ color: FAINT }}>每一项都是经过生产验证的真实能力</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div
                key={f.n}
                className="group relative p-7 rounded-lg border border-line bg-paper transition-colors duration-300"
                style={{ borderColor: HAIR }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand-300)' }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = HAIR }}
              >
                {/* Accent top line */}
                <div className="absolute top-0 left-6 right-6 h-[2px] rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `linear-gradient(90deg, ${A}, ${A2})` }} />
                {/* Watermark number */}
                <span className="absolute top-4 right-5 text-[56px] font-extrabold leading-none select-none" style={{ color: 'rgba(0,0,0,0.025)' }}>{f.n}</span>
                {/* Icon */}
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: 'var(--color-brand-50)' }}>
                  <svg className="w-5 h-5" fill="none" stroke={A} viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={f.icon} />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: serif }}>{f.title}</h3>
                <p className="text-base leading-relaxed" style={{ color: MUTED }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRODUCT LINES ═══ */}
      <section className="py-24" style={{ background: INK }}>
        <div className="max-w-[1080px] mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-medium tracking-[0.12em] uppercase mb-3" style={{ color: A }}>Product Lines</p>
            <h2 className="text-[32px] font-bold text-white" style={{ fontFamily: serif }}>一个平台，两条产品线</h2>
            <p className="text-lg mt-3" style={{ color: 'rgba(251,248,241,0.52)' }}>品牌线帮你"理解"数据，存储线帮你"保护"数据</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: '品牌线 · BrandMind', desc: '面向需要"理解"数据的客户。AI 记忆引擎深度分析每一份品牌素材，双模型交叉验证标注一致性并下调不可信结论。', items: ['7 步 AI 记忆蒸馏引擎', '第二模型族独立复核（不可用时降级为结构校验）', '以图搜图 + 语义 + 关键词搜索', '品牌画像 BMI 飞轮'] },
              { title: '存储线 · 安全存储', desc: '面向需要"保护"数据的客户。WORM 防篡改保险库确保数据不可篡改，3 级冷热分层使冷归档单价降至热存储的 1/8。', items: ['WORM 防篡改保险库（SHA-256）', '3 级冷热分层（OSS / TOS / 本地可配置）', '永久审计日志 + GDPR 合规', '保险柜与以图搜图（专业版起）'] },
            ].map((p, i) => (
              <div key={i} className="p-8 rounded-lg border transition-all duration-300" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.06)' }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-5" style={{ background: 'var(--color-brand-100)' }}>
                  <svg className="w-5 h-5" fill="none" stroke={A} viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={i === 0 ? 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' : 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4'} />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: serif }}>{p.title}</h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'rgba(251,248,241,0.60)' }}>{p.desc}</p>
                <div className="space-y-2.5">
                  {p.items.map((item, j) => (
                    <div key={j} className="flex items-center gap-2.5 text-sm" style={{ color: 'rgba(251,248,241,0.78)' }}>
                      <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: A }} />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ USE CASES ═══ */}
      <section className="py-24" style={{ background: SECTION_ALT }}>
        <div className="max-w-[1080px] mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-medium tracking-[0.12em] uppercase mb-3" style={{ color: A }}>Use Cases</p>
            <h2 className="text-[32px] font-bold" style={{ fontFamily: serif }}>覆盖六大核心行业</h2>
            <p className="text-lg mt-3" style={{ color: FAINT }}>从品牌方到医疗机构的真实场景</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {useCases.map((uc, i) => (
              <div
                key={i}
                className="p-6 rounded-lg border border-line bg-paper transition-colors duration-300"
                style={{ borderColor: HAIR }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand-300)' }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = HAIR }}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: 'var(--color-brand-50)', color: A }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={uc.icon} />
                  </svg>
                </div>
                <h3 className="text-base font-semibold mb-2">{uc.title}</h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: MUTED }}>{uc.desc}</p>
                <span className="inline-block text-2xs font-medium px-2.5 py-1 rounded-full" style={{ background: 'var(--color-brand-50)', color: A2 }}>
                  {uc.save}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section className="py-24" style={{ background: PAPER }} id="pricing">
        <div className="max-w-[1080px] mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-medium tracking-[0.12em] uppercase mb-3" style={{ color: A }}>Pricing</p>
            <h2 className="text-[32px] font-bold" style={{ fontFamily: serif }}>透明定价，按需选择</h2>
            <p className="text-lg mt-3" style={{ color: FAINT }}>以下套餐、价格与限额均由后端接口实时返回</p>
          </div>

          {/* Group tabs */}
          {groupKeys.length > 1 && (
            <div className="flex justify-center mb-12">
              <div className="inline-flex p-1 rounded-lg" style={{ background: HAIR_L }}>
                {groupKeys.map((k) => (
                  <button
                    key={k}
                    onClick={() => setActiveGroup(k)}
                    className="px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                    style={{
                      background: activeGroup === k ? PAPER : 'transparent',
                      color: activeGroup === k ? INK : FAINT,
                    }}
                  >
                    {planGroups[k].label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Cards */}
          {!planGroups ? (
            <div className="text-center py-14 text-sm" style={{ color: plansError ? 'var(--color-status-danger-strong)' : FAINT }}>
              {plansError ? '套餐数据暂不可用，请刷新页面重试' : '套餐加载中...'}
            </div>
          ) : (
            <>
              <div className="text-center text-sm mb-8" style={{ color: FAINT }}>
                {planGroups[activeGroup]?.description}
              </div>
              <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                {plans.map((t) => (
                  <div
                    key={t.plan}
                    className="relative p-6 rounded-lg border transition-all duration-300"
                    style={{ background: PAPER, borderColor: HAIR }}
                  >
                    <h3 className="text-base font-semibold mb-1">{t.label}</h3>
                    <div className="mb-1">
                      {t.contact_sales ? (
                        <span className="text-3xl font-extrabold tracking-tight">定制报价</span>
                      ) : t.price_monthly === 0 ? (
                        <span className="text-[32px] font-extrabold tracking-tight">免费</span>
                      ) : (
                        <>
                          <span className="text-sm" style={{ color: FAINT }}>¥</span>
                          <span className="text-[32px] font-extrabold tracking-tight">{t.price_monthly.toLocaleString('zh-CN')}</span>
                          <span className="text-sm" style={{ color: FAINT }}>/月</span>
                        </>
                      )}
                    </div>
                    <div className="text-xs mb-5" style={{ color: FAINT }}>
                      {t.contact_sales
                        ? '按实际需求出具报价'
                        : t.price_yearly > 0 ? `年付 ¥${t.price_yearly.toLocaleString('zh-CN')}` : '按月计费'}
                      {t.per_seat ? ' · 按席位' : ''}
                    </div>
                    <div className="space-y-2 text-sm mb-6" style={{ color: SOFT }}>
                      {(t.highlights || []).slice(0, 4).map((h, i) => (
                        <div key={i}>{h}</div>
                      ))}
                    </div>
                    <button
                      onClick={onOpenLogin}
                      className="w-full py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                      style={{ background: PAPER, color: MUTED, border: `1px solid ${HAIR}` }}
                    >
                      {t.contact_sales ? '联系销售' : t.price_monthly === 0 ? '免费开始' : '立即订阅'}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          <p className="text-center text-xs mt-10" style={{ color: FAINT }}>
            尊享版为定制报价 · 登录后点击订阅可提交升级申请
          </p>
        </div>
      </section>

      {/* ═══ SECURITY ═══ */}
      <section className="py-24" style={{ background: SECTION_ALT }}>
        <div className="max-w-[720px] mx-auto px-6 text-center">
          <p className="text-xs font-medium tracking-[0.12em] uppercase mb-3" style={{ color: A }}>Security</p>
          <h2 className="text-[32px] font-bold mb-4" style={{ fontFamily: serif }}>10 层安全防御</h2>
          <p className="text-lg mb-12" style={{ color: FAINT }}>从传输加密到容器隔离，每一层都有真实代码实现</p>
          <div className="flex flex-wrap justify-center gap-2.5">
            {['TLS 1.3', 'JWT', 'RBAC', '多租户隔离', 'Redis 限速', '4层文件扫描', '5层反幻觉', 'WORM', 'GDPR', '容器隔离'].map((item, i) => (
              <span key={i} className="px-4 py-2 rounded-full text-sm font-medium border" style={{ background: PAPER, borderColor: HAIR, color: MUTED }}>
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-24" style={{ background: INK }}>
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <h2 className="text-[36px] font-bold text-white mb-4" style={{ fontFamily: serif }}>准备好开始了吗？</h2>
          <p className="text-lg mb-10" style={{ color: 'rgba(251,248,241,0.52)' }}>免费版即刻体验 AI 品牌记忆引擎，无需信用卡</p>
          <button
            onClick={onOpenLogin}
            className="px-10 py-4 text-lg font-semibold text-white rounded-lg transition-all duration-300"
            style={{ background: `linear-gradient(135deg, ${A}, ${A2})`, boxShadow: ACCENT_SHADOW }}
          >
            免费注册，立即体验
          </button>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="py-8 border-t" style={{ background: INK, borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-[1080px] mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src={logoImg} alt="" className="h-5 opacity-60" />
            <span className="text-sm" style={{ color: 'rgba(251,248,241,0.42)' }}>正切科技 · 全景 OmniTuring</span>
          </div>
          <div className="text-2xs tracking-wide" style={{ color: 'rgba(251,248,241,0.32)' }}>
            &copy; 2026 TANGENT MARK. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}
